import {
  createContext,
  forwardRef,
  MouseEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import {
  ApiRestrictionData,
  AutocompleteOption,
  EntityType,
  ListControlRedifinitionDataType,
  ObjectDataRecord,
  ParameterPropertyType,
  POSTObjectDataFilter,
  TOuterData,
} from 'src/types'
import { v4 as uuid } from 'uuid'
import { ConfirmModal, FilePreview, Tooltip } from '@microservices/wiskey-react-components'
import { DataGrid, GridLoadingOverlay, MuiEvent } from '@microservices/wiskey-react-table-component'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import {
  Box,
  GlobalStyles,
  Grid,
  IconButton,
  IconButtonProps,
  Skeleton,
  styled,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { DataGridProps, GridRowParams } from '@mui/x-data-grid'

import { CustomColumnMenuComponent } from '@pages/ConfiguredEntity/components/CustomColumnMenuComponent'
import { CustomPanelComponent } from '@pages/ConfiguredEntity/components/CustomPanelComponent'
import { CustomToolbarComponent } from '@pages/ConfiguredEntity/components/CustomToolbarComponent'
import { UpdateSettings } from '@pages/ConfiguredEntity/components/UpdateSettings/UpdateSettings'
import { generateHeaderGroupStyles, getColumnGroupingModel } from '@pages/ConfiguredEntity/helpers'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { ModalWrapper } from '@components/ModalWrapper'
import { PageTitle } from '@components/PageTitle'
import { PopperContextMenu } from '@components/PopperContextMenu'
import { QuickSearch } from '@components/QuickSearch'
import { QuickSearchInDDW } from '@components/QuickSearchInDDW'
import { RedirectToConfigButton } from '@components/RedirectToConfigButton'

import { ActualTimeWindowInterval, ScrollingParams } from '@hooks'
import { ReturnTransformCommonParametersToValues } from '@helpers'
import {
  CLICK_EVENT_TYPE,
  CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME,
  DATAGRID_HEADER_HEIGHT,
  DATAGRID_HEADER_WINDOW_HEIGHT,
  DATAGRID_ROW_WINDOW_HEIGHT,
  DIALOG_WINDOW_BOTTOM_ACTION_BAR,
  DIALOG_WINDOW_HEADER_HEIGHT,
  ENTITY,
  FIELD_VALUE_TYPE,
  FLEXIBLE,
  HEIGHT_EMPTY_TABLE_SPACE,
  MIN_COLLAPSED_LC_HEIGHT,
  MIN_HEIGHT_VIEW,
  PreFillType,
  QUICK_SEARCH_DDW,
  SEARCH_ASSISTANT_VIEW,
  VIEW_ROWS_AMOUNT,
  VISIBLE_HIDDEN,
} from '@constants'

import { LocalEventBus } from '../../events'

import { CustomNoRowsOverlay } from './components/CustomNoRowsOverlay'
import { DraggableRowImage } from './components/DraggableRowImage'
import { RowStylesWrapper } from './components/RowStylesWrapper'
import { DynamicForms, StaticForms } from './components/SamePageForms'
import { ConfiguredEntityCancelButton } from './CancelButton'
import { ConfiguredEntityCreateButton } from './CreateButton'
import { ConfiguredEntityCreateViewRowButton } from './CreateViewRowButton'
import { ConfiguredEntityEditButton } from './EditButton'
import { useConfiguredEntity } from './hooks'
import { ConfiguredEntitySaveButton } from './SaveButton'
import { SaveUserRestrictionsButton } from './SaveUserRestrictionsButton'
import { OpenFormProps, PageContextType } from './types'

const getRowId = (item: ObjectDataRecord) => {
  return item[CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID_FIELD_NAME]
}

export type ConfiguredEntityProps = {
  hasSearchAssistant?: boolean
  windowHeight?: number
  entityCode: string | null
  entityId: number | string | null
  path: string
  title: string
  titleHidden?: boolean
  expandable?: boolean
  isNestedView?: boolean
  parentFormIsDirty?: boolean
  getLeaveForm?: () => boolean
  onResetEditForm?: () => void
  onSelectPickerOption?: (row: ObjectDataRecord) => void
  onExpandedChange?: (height: number) => void
  pageSize?: number
  type: ENTITY
  parentFormObjectId?: string
  parentFormObjectCode?: string
  parentFormElementId?: number
  parentFormObjectIdType?: FIELD_VALUE_TYPE
  valueId?: number
  fieldId?: number
  columnId?: number
  generalFilterField?: string
  selectedPickerOption?: string
  selectedPickerOptionValue?: string | AutocompleteOption
  onMultiSelectPickerOption?: (rows: ObjectDataRecord[]) => void
  multipleSelectPickerOption?: AutocompleteOption[]
  objectValueNameOption?: string
  onOpenForm?: (
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    type: CLICK_EVENT_TYPE
  ) => void
  search?: string
  pathname?: string
  enableStaticForms?: boolean
  dialogId?: string
  isListControlDialogWindow?: boolean
  isDropDownDialogWindow?: boolean
  isSearchAssistantDialogWindow?: boolean
  onSetEntity: (value: EntityType | null) => void
  entity: EntityType | null
  isViewDialogWindow?: boolean
  readonly?: boolean
  showQuickSearch?: boolean
  defaultFilter?: string
  minHeight?: number | string
  outerRequestData?: ListControlRedifinitionDataType
  prefillCreateObjectRecordInForm?: PreFillType // Предзаполнение данных формы из другого объекта при создании записи
  parentFormIsSectionWindow?: boolean
  outerData?: TOuterData
  outerEventBus?: LocalEventBus
  onRowClick?: (params: GridRowParams<ObjectDataRecord>, event: MuiEvent<MouseEvent>) => void
  onRowSelection?: (selectedRow: ObjectDataRecord | null) => void
  onSortChange?: () => void // Callback при изменении сортировки для перезапуска временного окна
  initialCommonParameters?: ReturnTransformCommonParametersToValues
  initialRestrictions?: ApiRestrictionData
  globalColumnTextStyles?: ParameterPropertyType[]
  globalColumnHeaderStyles?: ParameterPropertyType[]
}

export const PageContext = createContext<PageContextType>({} as PageContextType)

type ExpandMoreProps = {
  expand: boolean
} & IconButtonProps

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props

  return <IconButton {...other} />
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

export type GetConfiguredEntityParamsHandle = {
  refreshObjectData: (
    params?: Omit<ScrollingParams, 'page'>,
    skip?: boolean,
    timeWindow?: ActualTimeWindowInterval
  ) => void
  handleClickSearchFilter: (
    body: POSTObjectDataFilter,
    timeWindow?: ActualTimeWindowInterval
  ) => void
}

export const ConfiguredEntity = forwardRef<GetConfiguredEntityParamsHandle, ConfiguredEntityProps>(
  (props, ref) => {
    const {
      hasSearchAssistant,
      windowHeight: propsWindowHeight = 0,
      entityCode,
      entityId,
      path,
      title,
      titleHidden,
      expandable,
      isNestedView = false,
      parentFormIsDirty,
      getLeaveForm,
      onResetEditForm,
      onSelectPickerOption,
      pageSize: defaultPageSize = VIEW_ROWS_AMOUNT,
      type,
      parentFormObjectCode,
      parentFormObjectId,
      parentFormElementId,
      parentFormObjectIdType,
      fieldId,
      columnId,
      generalFilterField,
      valueId,
      selectedPickerOption,
      selectedPickerOptionValue,
      onMultiSelectPickerOption,
      multipleSelectPickerOption,
      objectValueNameOption,
      onExpandedChange,
      onOpenForm,
      search,
      pathname = '',
      enableStaticForms,
      dialogId,
      isListControlDialogWindow = false,
      isDropDownDialogWindow = false,
      isSearchAssistantDialogWindow = false,
      onSetEntity,
      entity,
      isViewDialogWindow = false,
      readonly,
      showQuickSearch = true,
      defaultFilter,
      minHeight = 0,
      outerRequestData = {},
      prefillCreateObjectRecordInForm,
      onRowClick,
      onRowSelection,
      onSortChange,
      initialRestrictions,
      initialCommonParameters,
      globalColumnHeaderStyles,
      globalColumnTextStyles,
      outerData = {},
      outerEventBus,
    } = props

    const instanceId = useMemo(() => {
      return uuid()
    }, [])

    const { t } = useTranslation()
    const theme = useTheme()

    const [expanded, setExpanded] = useState(true)

    const containerRef = useRef<HTMLDivElement>(null)
    const topEntityContentRef = useRef<HTMLDivElement>(null)
    const draggableRowImageRef = useRef<HTMLDivElement>(null)

    const windowHeight = useMemo(
      () => propsWindowHeight || containerRef?.current?.clientHeight || 0,
      [containerRef.current?.clientHeight, propsWindowHeight]
    )

    const TOP_ENTITY_CONTENT_HEIGHT_VIEW = isViewDialogWindow
      ? (topEntityContentRef.current?.offsetHeight || 0) +
        DATAGRID_HEADER_WINDOW_HEIGHT +
        SEARCH_ASSISTANT_VIEW +
        DIALOG_WINDOW_HEADER_HEIGHT -
        HEIGHT_EMPTY_TABLE_SPACE
      : (topEntityContentRef.current?.offsetHeight || 0) + DATAGRID_HEADER_WINDOW_HEIGHT

    const TOP_ENTITY_CONTENT_HEIGHT_DROPDOWN =
      DIALOG_WINDOW_HEADER_HEIGHT +
      DATAGRID_HEADER_WINDOW_HEIGHT +
      DIALOG_WINDOW_BOTTOM_ACTION_BAR +
      QUICK_SEARCH_DDW

    const TOP_ENTITY_CONTENT_HEIGHT = isDropDownDialogWindow
      ? TOP_ENTITY_CONTENT_HEIGHT_DROPDOWN
      : TOP_ENTITY_CONTENT_HEIGHT_VIEW

    const pageSizeByWindowHeight = useMemo(
      () =>
        windowHeight
          ? Math.floor((windowHeight - TOP_ENTITY_CONTENT_HEIGHT) / DATAGRID_ROW_WINDOW_HEIGHT)
          : 0,
      [TOP_ENTITY_CONTENT_HEIGHT, windowHeight]
    )

    const customPageSize = useMemo(
      () => (pageSizeByWindowHeight > defaultPageSize ? pageSizeByWindowHeight : defaultPageSize),
      [defaultPageSize, pageSizeByWindowHeight]
    )

    const { state, data, handlers } = useConfiguredEntity({
      hasSearchAssistant,
      entityCode,
      entityId,
      path,
      isNestedView,
      getLeaveForm,
      onResetEditForm,
      onSelectPickerOption,
      parentFormIsDirty,
      type,
      parentFormObjectCode,
      parentFormObjectId,
      parentFormElementId,
      parentFormObjectIdType,
      fieldId,
      columnId,
      generalFilterField,
      valueId,
      selectedPickerOption,
      selectedPickerOptionValue,
      onMultiSelectPickerOption,
      multipleSelectPickerOption,
      objectValueNameOption,
      handleOpenForm: onOpenForm,
      search,
      pathname,
      dialogId,
      isListControlDialogWindow,
      customPageSize,
      onSetEntity,
      entity,
      readonly,
      isViewDialogWindow,
      defaultFilter,
      outerRequestData,
      showQuickSearch,
      expanded,
      prefillCreateObjectRecordInForm,
      initialRestrictions,
      initialCommonParameters,
      globalColumnHeaderStyles,
      globalColumnTextStyles,
      outerData,
      outerEventBus,
      onRowClick,
      onRowSelection,
      onSortChange,
      draggableRowImageRef,
      instanceId,
    })

    const {
      isCtrlHeld,
      isShiftHeld,
      selectedRowIds,
      samePageFormsData,
      currentStaticForm,
      currentDynamicForm,
      globalStyles,
      isListControl,
      columnVisibilityModel,
      columnNameCheckBox,
      visibleColumns,
      sortKeysForQuickSearch,
      sortKeysColumnCheckBox,
      pinnedColumns,
      scrollingParams,
      viewColumns,
      isBooleanSearchMode,
      configActions,
      contextMenuData,
      onRefContextMenu,
      position,
      isShowDeleteModal,
      tooltipPosition,
      tooltipData,
      isUpdateLoading,
      showPreviewFileModal,
      previewFileUrl,
      isEditMode,
      hasUnsavedChanges,
      hasNewRows,
      hasExternalActionData,
      isActiveTimeWindow,
      allowEditMode,
      hasErrorFields,
      isPrefillDataLoading,
      isCtrlPressed,
      isShowUserRestrictionAsDefaultModel,
      isLoadingRestrictions,
      isDropDownWindowOpen,
      countSelectedRowWithoutDraggableRow,
      displayDraggableRowImageText,
      hasDragStartEvent,
    } = state

    const {
      isAssistiantSearch,
      objects,
      actions,
      objectCode,
      columns,
      isForm,
      isSearchResult,
      rowCreateAction,
      hasRowAction,
      shouldDisplayCreateButton,
      shouldDisplayNewLineButton,
      currentSort,
      objectData,
      totalPages,
      totalElements,
      isLoadingObjectData,
      apiRef,
      staticFormsData,
      dynamicFormsData,
      areAllColumnsHidden,
      showColumnsButton,
      isColumnReorderEnabled,
      valueSearchFilter,
      searchRule,
      hasQuickSearch,
      hasWrapRowText,
      hasWrapHeaderText,
      headerHeight,
      headerGroupHeight,
      isEnabledLocalParameters,
      columnsExist,
      isDragActive,
    } = data

    const {
      handleChangeModelSort,
      handleClickSearchFilter,
      handleSetSelectedRowIds,
      handleStaticFormChange,
      handleDynamicFormChange,
      handleRemoveSamePageForm,
      handleCloseCurrentFormTab,
      handleSetOpenedContainersInStaticForms,
      handleRowClick,
      handleCellKeyDown,
      handleCellClick,
      handleCellDoubleClick,
      handleCellEditStop,
      handleCreate,
      handleGetRowClassName,
      handleObjectDataPageChange,
      handleSetObjectData,
      handleGetCellClassName,
      handleGetRowHeight,
      handleColumnWidthChange,
      handleUpdateSamePageFormData,
      handleOpenShowColumnsSettings,
      handlePinnedColumnsChange,
      handleResetRestrictions,
      handleChangeColumnVisibilityModel,
      handleSetGlobalFilter,
      handleSetSearchRule,
      handleSetColumnNameCheckBox,
      handleSetSortKeysColumnCheckBox,
      handleSetBooleanMode,
      handleContextMenu,
      onCloseContextMenu,
      handleClickDelete,
      handleCloseDeleteModal,
      handleOpenTooltip,
      handleCloseTooltip,
      refreshObjectData,
      handleRefreshAllPages,
      handleObjectSubscribe,
      handleClosePreviewFileModal,
      setIsEditMode,
      handleViewRowUpdate,
      handleDiscardViewEditChanges,
      handleSaveViewEditChanges,
      handleAddEmptyRow,
      handleUseUserRestrictionsAsDefault,
      handleCloseUserRestrictionAsDefaultModel,
      handleShowUserRestrictionAsDefaultModel,
      handleOnDragStart,
      handleOnDragEnd,
      handleOnDragOver,
      handleOnDrop,
      handleOnDragLeave,
      handleSetLocalUpdateType,
    } = handlers

    useImperativeHandle(ref, () => ({
      refreshObjectData,
      handleClickSearchFilter,
    }))

    const contextValue: PageContextType = {
      isBooleanSearchMode,
      columnNameCheckBox,
      sortKeysForQuickSearch,
      sortKeysColumnCheckBox,
      searchRule,
      visibleColumns,
      entityCode,
      entityId,
      currentSort: currentSort || [],
      columns,
      path,
      rows: objectData,
      title: title || '',
      countPage: totalPages,
      actions: actions ?? [],
      isLoadingObjectData,
      objectCode,
      apiRef,
      type,
      isAssistiantSearch,
      objects: objects || [],
      isCtrlHeld,
      isShiftHeld,
      samePageFormsData,
      selectedRowIds,
      currentStaticForm,
      currentDynamicForm,
      staticFormsData,
      dynamicFormsData,
      hasRowAction,
      isViewDialogWindow,
      dialogId,
      valueSearchFilter,
      scrollingParams,
      onSetSorting: handleChangeModelSort,
      handleRemoveSamePageForm,
      handleUpdateSamePageFormData,
      onSetOpenedContainersInStaticForms: handleSetOpenedContainersInStaticForms,
      onSetAllObjectData: handleSetObjectData,
      onSetSelectedRowId: handleSetSelectedRowIds,
      handleCloseCurrentFormTab,
      handleStaticFormChange,
      handleDynamicFormChange,
      handleSetBooleanMode,
      handleSetGlobalFilter,
      handleResetRestrictions,
      handleSetLocalUpdateType,
    }

    const handleExpandClick = () => {
      const newExpanded = !expanded
      setExpanded(newExpanded)
      const newHeight = newExpanded ? MIN_HEIGHT_VIEW : MIN_COLLAPSED_LC_HEIGHT
      onExpandedChange?.(newHeight)
    }

    const Title = () =>
      useMemo(
        () =>
          type === ENTITY.LIST_CONTROL ? (
            <Typography sx={{ fontSize: '1rem' }}>
              {title}: {totalElements || 0}
            </Typography>
          ) : (
            <Box>{title}</Box>
          ),
        [type, title, totalElements, expanded]
      )

    const columnEditingSlots: Pick<DataGridProps, 'slots'> = {
      slots: {
        columnMenu: props => (
          <CustomColumnMenuComponent
            {...props}
            apiRef={apiRef}
            handleResetRestrictions={handleResetRestrictions}
            hideShowOptions={type === ENTITY.DROP_DOWN_ENTITY}
            onShowConfig={handleOpenShowColumnsSettings}
          />
        ),
        toolbar: () => (
          <CustomToolbarComponent showColumnsButton={showColumnsButton && columnsExist} />
        ),
        panel: CustomPanelComponent,
      },
    }

    const userSettingsSlots =
      type === ENTITY.VIEW || type === ENTITY.LIST_CONTROL || type === ENTITY.DROP_DOWN_ENTITY
        ? columnEditingSlots
        : {}

    const isCreateButton = isSearchResult && shouldDisplayCreateButton && isAssistiantSearch
    const hasScrollbar = objectData.length > customPageSize
    const hasColumnGroups = Boolean(entity?.columnHeaders.length)
    const groupedColumns = useMemo(
      () => (hasColumnGroups ? entity?.columnHeaders.flatMap(group => group.children) : null),
      [entity?.columnHeaders, hasColumnGroups]
    )
    const hasVisibleGroups = useMemo(
      () => groupedColumns?.some(col => columnVisibilityModel[col]),
      [columnVisibilityModel, groupedColumns]
    )

    const loading = isLoadingObjectData
    const showTooltip = Boolean(tooltipPosition && !loading && objectData.length)
    const defaultHeaderHeight =
      isViewDialogWindow || isListControlDialogWindow || isDropDownDialogWindow
        ? DATAGRID_HEADER_WINDOW_HEIGHT
        : DATAGRID_HEADER_HEIGHT
    const dataGridHeaderHeight =
      headerHeight && isEnabledLocalParameters ? headerHeight : defaultHeaderHeight
    const dataGridHeaderGroupHeight = hasVisibleGroups
      ? headerGroupHeight && isEnabledLocalParameters
        ? headerGroupHeight
        : defaultHeaderHeight
      : 0

    const totalCountElement = (
      <Typography fontSize='1rem' pb={isViewDialogWindow ? 0 : 1} pr={1}>
        {t('searchAssistant.result', { totalElements })}
      </Typography>
    )

    return (
      <PageContext.Provider value={contextValue}>
        {hasDragStartEvent && (
          <Box left={'-9999px'} position={'absolute'} zIndex={10}>
            <DraggableRowImage
              ref={draggableRowImageRef}
              countAdditionalRows={countSelectedRowWithoutDraggableRow}
              displayText={displayDraggableRowImageText}
              getHeightElement={handleGetRowHeight}
            />
          </Box>
        )}
        {(configActions.actionsAvailable.on_column_right_click ||
          configActions.actionsAvailable.on_column_right_click) && (
          <PopperContextMenu
            dataTransfer={contextMenuData.dataTransfer}
            items={contextMenuData.items}
            position={position}
            onClose={onCloseContextMenu}
            onItemClick={contextMenuData.onItemClick}
            onRefContextMenu={onRefContextMenu}
          />
        )}
        {showTooltip &&
          createPortal(
            <Tooltip
              lines={tooltipData?.items ?? []}
              loading={tooltipData?.loading}
              metaData={tooltipData?.metaData ?? []}
              position={tooltipPosition}
              width={tooltipData?.width ?? null}
            />,
            document.body
          )}
        <GlobalStyles
          styles={{
            ...globalStyles,
            '.row-drag-over.row-drag-over.row-drag-over > .MuiDataGrid-cell': {
              backgroundColor: `${theme.palette.primary.main} !important`,
              color: `${theme.palette.primary.contrastText} !important`,
            },
          }}
        />
        {type !== ENTITY.DROP_DOWN_ENTITY && (
          <Outlet
            context={{
              handleRemoveSamePageForm,
              handleUpdateSamePageFormData,
              handleCloseCurrentFormTab,
              entityType: type,

              onSetOpenedContainersInStaticForms: handleSetOpenedContainersInStaticForms,

              samePageFormsData,
              selectedRowIds,
            }}
          />
        )}
        <Box
          ref={containerRef}
          sx={{
            ...FLEXIBLE,
            ...(isForm && !isNestedView && type !== ENTITY.DROP_DOWN_ENTITY && VISIBLE_HIDDEN),
            ...(isNestedView && { mb: 0 }),
            ...(isListControl && { cursor: 'pointer' }),
            height: '100%',
            minHeight,
            ...(expanded ? {} : { maxHeight: 56 }),
          }}
        >
          {dynamicFormsData.length && hasRowAction && type !== ENTITY.DROP_DOWN_ENTITY ? (
            <DynamicForms />
          ) : (
            <></>
          )}
          {/* {isShiftHeld && <DynamicForms />} */}
          {type !== ENTITY.DROP_DOWN_ENTITY && expandable && (
            <PageTitle
              end={
                <Box mr={isViewDialogWindow ? 1 : 0.5}>
                  {isListControl && (
                    <ExpandMore expand={expanded} onClick={handleExpandClick}>
                      <ExpandMoreIcon />
                    </ExpandMore>
                  )}
                </Box>
              }
              sx={{
                margin: `${isViewDialogWindow ? '4px 0' : '16px 0 8px 0'}`,
                ...(isNestedView && { mb: 0 }),
                ...(isSearchAssistantDialogWindow && { m: 0 }),
              }}
              {...(isListControl && {
                onClick: handleExpandClick,
                start: (
                  <Grid container direction={'column'}>
                    <Grid item mb={0} px='10px'>
                      {!titleHidden && (
                        <Typography variant={'h6'}>{title ? <Title /> : <Skeleton />}</Typography>
                      )}
                    </Grid>
                  </Grid>
                ),
              })}
            />
          )}
          {isListControl && !expandable && !titleHidden && (
            <Typography variant={'h6'}>{title ? <Title /> : <Skeleton />}</Typography>
          )}
          <PageContentLayout isBreadcrumbs hidden={isNestedView} sx={FLEXIBLE}>
            {expanded && (
              <Box sx={FLEXIBLE}>
                <Box
                  sx={{
                    ...FLEXIBLE,
                    p: isNestedView || type === ENTITY.DROP_DOWN_ENTITY ? 0 : 1,
                    pt: isAssistiantSearch ? 1 : 0,
                  }}
                >
                  <Box ref={topEntityContentRef}>
                    {type === ENTITY.DROP_DOWN_ENTITY && entity && (
                      <Box display={'flex'} justifyContent={'space-between'}>
                        <QuickSearchInDDW setGlobalFilter={handleSetGlobalFilter} />
                        <RedirectToConfigButton
                          code={entity?.code ?? entityCode}
                          entityType={type}
                          type='entity'
                        />
                        <SaveUserRestrictionsButton
                          loading={isLoadingRestrictions}
                          onClick={handleShowUserRestrictionAsDefaultModel}
                        />
                      </Box>
                    )}
                    {type !== ENTITY.DROP_DOWN_ENTITY && (
                      <Grid container alignItems='center' justifyContent='space-between'>
                        <Grid item alignItems='center' display='flex'>
                          {hasQuickSearch && entity && (
                            <Box sx={{ mt: 0 }}>
                              <QuickSearch
                                columnNameCheckBox={columnNameCheckBox}
                                columns={viewColumns}
                                dialogId={dialogId}
                                entityId={entityId}
                                isBooleanSearchMode={isBooleanSearchMode}
                                isDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                                loading={isLoadingObjectData || isLoadingRestrictions}
                                scrollingParams={scrollingParams}
                                searchRule={searchRule}
                                searchValue={valueSearchFilter}
                                setBooleanMode={handleSetBooleanMode}
                                setColumnNameCheckBox={handleSetColumnNameCheckBox}
                                setGlobalFilter={handleSetGlobalFilter}
                                setSearchRule={handleSetSearchRule}
                                setSortKeysForQuickSearch={handleSetSortKeysColumnCheckBox}
                                sortKeysColumnCheckBox={sortKeysColumnCheckBox}
                              />
                            </Box>
                          )}
                          {(type === ENTITY.VIEW || isSearchResult) &&
                            (loading ? (
                              <Skeleton variant='text'>{totalCountElement}</Skeleton>
                            ) : (
                              totalCountElement
                            ))}
                        </Grid>
                        <Grid item alignItems={'center'} display={'flex'}>
                          {entity && (
                            <RedirectToConfigButton
                              code={entity?.code ?? entityCode}
                              entityType={type}
                              type='entity'
                            />
                          )}

                          {entity && (
                            <UpdateSettings
                              entity={entity}
                              isActiveTimeWindow={isActiveTimeWindow}
                              isUpdateLoading={isUpdateLoading}
                              position={position}
                              onObjectSubscribe={handleObjectSubscribe}
                              onRefreshAllPages={handleRefreshAllPages}
                            />
                          )}
                          <SaveUserRestrictionsButton
                            loading={isLoadingRestrictions}
                            onClick={handleShowUserRestrictionAsDefaultModel}
                          />
                          {((shouldDisplayCreateButton && !isAssistiantSearch) ||
                            isCreateButton) && (
                            <ConfiguredEntityCreateButton
                              isViewDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                              text={rowCreateAction?.title}
                              onCreate={handleCreate}
                            />
                          )}
                          {!isEditMode && allowEditMode && (
                            <ConfiguredEntityEditButton
                              isViewDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                              onEdit={() => setIsEditMode(true)}
                            />
                          )}
                          {(isEditMode || hasNewRows) && (
                            <>
                              <ConfiguredEntitySaveButton
                                disabled={Boolean(!hasUnsavedChanges || hasErrorFields)}
                                isViewDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                                onSave={() => handleSaveViewEditChanges(hasNewRows)}
                              />
                              <ConfiguredEntityCancelButton
                                isViewDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                                onCancel={() => handleDiscardViewEditChanges(hasUnsavedChanges)}
                              />
                            </>
                          )}
                          {shouldDisplayNewLineButton && allowEditMode && (
                            <ConfiguredEntityCreateViewRowButton
                              isViewDialogWindow={isViewDialogWindow || isListControlDialogWindow}
                              loading={isPrefillDataLoading}
                              onAddEmptyRow={handleAddEmptyRow}
                            />
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                  <Grid container flexWrap={'nowrap'} spacing={2} sx={FLEXIBLE}>
                    <Grid item sx={FLEXIBLE}>
                      <RowStylesWrapper
                        globalStyles={globalStyles}
                        hasScrollbar={hasScrollbar}
                        hasWrapHeaderText={hasWrapHeaderText}
                        hasWrapRowText={hasWrapRowText}
                        headerGroupHeight={dataGridHeaderGroupHeight}
                        headerHeight={dataGridHeaderHeight}
                        isEnabledLocalParameters={isEnabledLocalParameters}
                        objectData={objectData}
                        pageSize={customPageSize}
                      >
                        <DataGrid
                          {...((isViewDialogWindow ||
                            isListControlDialogWindow ||
                            isDropDownDialogWindow) && {
                            columnHeaderHeight: dataGridHeaderHeight,
                            columnGroupHeaderHeight: dataGridHeaderGroupHeight,
                          })}
                          {...((isViewDialogWindow ||
                            isListControlDialogWindow ||
                            isDropDownDialogWindow) && {
                            rowHeight: DATAGRID_ROW_WINDOW_HEIGHT,
                          })}
                          enableCellSeparator
                          enableMultiSort
                          hideFooter
                          apiRef={apiRef}
                          columnGroupingModel={getColumnGroupingModel(entity?.columnHeaders)}
                          columnVisibilityModel={columnVisibilityModel}
                          columns={columns}
                          disableColumnMenu={isEditMode || isLoadingRestrictions || loading}
                          disableColumnPinning={false}
                          disableColumnReorder={!isColumnReorderEnabled}
                          disableColumnResize={loading}
                          disableColumnSelector={isEditMode}
                          disableColumnSorting={isEditMode || isLoadingRestrictions}
                          disableVirtualization={isDragActive}
                          editMode='cell'
                          fetchNextPage={handleObjectDataPageChange}
                          getCellClassName={handleGetCellClassName}
                          getRowClassName={handleGetRowClassName}
                          getRowHeight={handleGetRowHeight}
                          getRowId={getRowId}
                          loading={loading}
                          pageCount={totalPages}
                          paginationModel={{ page: 0, pageSize: customPageSize }}
                          pinnedColumns={objectData.length > 0 ? pinnedColumns : undefined}
                          processRowUpdate={handleViewRowUpdate}
                          rowBufferPx={2000} // to do Дефект персчета высоты row на стороне MUI data grid. Нужен issue
                          rowHoverCursor={hasRowAction ? 'pointer' : 'default'}
                          rows={objectData}
                          sx={{
                            opacity: 1,
                            pointerEvents: 'auto',
                            '& .MuiDataGrid-menuIcon .MuiButtonBase-root, & .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
                              {
                                mt: '4px',
                              },
                            '& .MuiDataGrid-columnHeader--emptyGroup': {
                              '& .MuiDataGrid-columnSeparator': {
                                display: 'none',
                              },
                            },
                            '& .MuiDataGrid-columnHeaders': {
                              '& .MuiButtonBase-root': {
                                padding: '3px',
                              },
                              '& .MuiDataGrid-menuIcon svg': {
                                fontSize: '1rem',
                              },
                              '& .MuiDataGrid-iconButtonContainer svg': {
                                fontSize: '0.85rem',
                              },
                            },
                            ...(isEditMode || hasNewRows
                              ? {
                                  '.MuiDataGrid-cell:focus-within': {
                                    outline: '1px solid black',
                                  },
                                  '.MuiDataGrid-cell:focus': {
                                    outline: '1px solid black',
                                  },
                                }
                              : {}),
                            ...(objectData.length === 0
                              ? {
                                  '.MuiDataGrid-columnSeparator': { display: 'none' },
                                  row: {
                                    transition: '0.2s ease-in',
                                  },
                                  ...(areAllColumnsHidden && {
                                    // TODO columnHeadersInner и pinnedColumnHeaders возможно удалён
                                    '.MuiDataGrid-pinnedColumnHeaders, .MuiDataGrid-columnHeadersInner':
                                      { visibility: 'hidden' },
                                  }),
                                }
                              : {}),
                            ...generateHeaderGroupStyles(entity?.columnHeaders),
                          }}
                          // dataGridBoxSx={
                          //   type === ENTITY.DROP_DOWN_ENTITY ? { height: tableHeight } : {}
                          // }
                          sortModel={currentSort}
                          sortingMode={'server'}
                          visibilityBadgeSort={pathname.includes('forms') && !isNestedView}
                          slotProps={{
                            cell: {
                              onMouseOver: e =>
                                !isEditMode &&
                                !hasNewRows &&
                                !isCtrlPressed &&
                                !isDropDownWindowOpen &&
                                handleOpenTooltip(e),
                              onMouseLeave: handleCloseTooltip,
                            },
                            row: {
                              draggable: true,
                              onContextMenu: handleContextMenu,
                              onMouseOver: e =>
                                !isEditMode &&
                                !hasNewRows &&
                                !isCtrlPressed &&
                                !isDropDownWindowOpen &&
                                handleOpenTooltip(e),
                              onMouseLeave: handleCloseTooltip,
                              onDragStart: handleOnDragStart,
                              onDragEnd: handleOnDragEnd,
                              onDragOver: handleOnDragOver,
                              onDragLeave: handleOnDragLeave,
                              onDrop: handleOnDrop,
                            },
                            columnMenu: {
                              onBlur: () => {
                                apiRef.current.hideColumnMenu?.()
                              },
                            },
                          }}
                          slots={{
                            toolbar: CustomToolbarComponent,
                            loadingOverlay: GridLoadingOverlay,
                            noRowsOverlay: CustomNoRowsOverlay,
                            ...userSettingsSlots.slots,
                          }}
                          onCellClick={handleCellClick}
                          onCellDoubleClick={handleCellDoubleClick}
                          onCellEditStop={handleCellEditStop}
                          onCellKeyDown={handleCellKeyDown}
                          onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                          onColumnWidthChange={handleColumnWidthChange}
                          onPinnedColumnsChange={handlePinnedColumnsChange}
                          onSortModelChange={handleChangeModelSort}
                          // pageSize={customPageSize}
                          onRowClick={handleRowClick}
                          // disableColumnReorder={isNestedView}
                          onRowDoubleClick={handleRowClick}
                        />
                      </RowStylesWrapper>
                    </Grid>
                    {enableStaticForms && staticFormsData.length && hasRowAction ? (
                      <Grid item>
                        <StaticForms />
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Box>
              </Box>
            )}
          </PageContentLayout>
          {enableStaticForms &&
          staticFormsData.length &&
          hasRowAction &&
          type !== ENTITY.DROP_DOWN_ENTITY ? (
            <Box sx={{ minHeight: 500 }} />
          ) : (
            <></>
          )}
        </Box>
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={isShowDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleCloseDeleteModal}
          onConfirm={handleClickDelete}
        />
        <ConfirmModal
          actionBtnText={t('modal.saveBtnText')}
          isShow={isShowUserRestrictionAsDefaultModel}
          text={t('modal.useUserRestrictions.text')}
          title={t('modal.useUserRestrictions.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
          }}
          onClose={handleCloseUserRestrictionAsDefaultModel}
          onConfirm={handleUseUserRestrictionsAsDefault}
        />
        {/* TODO: Заменить на компонент из kit'а и добавить возможность не отображать footer */}
        <ModalWrapper
          fullViewportHeight
          hideActions
          hideDivider
          isShow={showPreviewFileModal}
          onClose={handleClosePreviewFileModal}
        >
          {previewFileUrl && <FilePreview url={previewFileUrl} />}
        </ModalWrapper>
      </PageContext.Provider>
    )
  }
)

ConfiguredEntity.displayName = 'ConfiguredEntity'
